import {
  Bell,
  ChevronDown,
  ClipboardList,
  Globe,
  IdCard,
  LayoutDashboard,
  LogOut,
  UserRound,
} from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, redirect, useLoaderData, useNavigate } from 'react-router';

import { Layout as SharedLayout } from '@cold-ambulance/layout';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@cold-ambulance/shadcn';
import {
  AcceptTermsDialog,
  logout,
  useAction,
  useData,
} from '@cold-ambulance/ui';

import { requiresAuth } from './auth-loader.ts';
import { auth, useUser } from './firebase.tsx';

function DefaultLogo(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <circle cx="22" cy="22" r="22" fill="#333E75" />
      <path d="M20.3838 26.8006H22.6702V31H20.3838V26.8006Z" fill="white" />
      <path
        d="M24.7178 12.6805C24.7178 12.3062 24.4116 12 24.0373 12H19.0154C18.6412 12 18.335 12.3062 18.335 12.6805V13.1568H24.7178V12.6805Z"
        fill="white"
      />
      <path
        d="M12 18.5463V30.3186C12 30.6929 12.3062 30.9991 12.6805 30.9991H14.5382V17.8658H12.6805C12.3062 17.8658 12 18.1721 12 18.5463Z"
        fill="white"
      />
      <path
        d="M30.3733 17.8658H28.5156V30.9991H30.3733C30.7476 30.9991 31.0538 30.6929 31.0538 30.3186V18.5463C31.0538 18.1721 30.7476 17.8658 30.3733 17.8658Z"
        fill="white"
      />
      <path
        d="M26.4743 14.5194H16.5799C16.2039 14.5194 15.8994 14.8239 15.8994 15.1999V31H19.0229V26.8006C19.0229 26.0521 19.6285 25.4396 20.3839 25.4396H22.6703C23.4256 25.4396 24.0313 26.0521 24.0313 26.8006V31H27.1547V15.1999C27.1547 14.8239 26.8502 14.5194 26.4743 14.5194ZM20.1661 22.9744H18.6214C18.2454 22.9744 17.9409 22.6699 17.9409 22.2939C17.9409 21.918 18.2454 21.6135 18.6214 21.6135H20.1661C20.5421 21.6135 20.8466 21.918 20.8466 22.2939C20.8466 22.6691 20.5421 22.9744 20.1661 22.9744ZM20.1661 20.4489H18.6214C18.2454 20.4489 17.9409 20.1444 17.9409 19.7684C17.9409 19.3924 18.2454 19.0879 18.6214 19.0879H20.1661C20.5421 19.0879 20.8466 19.3924 20.8466 19.7684C20.8466 20.1444 20.5421 20.4489 20.1661 20.4489ZM20.1661 17.9234H18.6214C18.2454 17.9234 17.9409 17.6189 17.9409 17.2429C17.9409 16.8669 18.2454 16.5624 18.6214 16.5624H20.1661C20.5421 16.5624 20.8466 16.8669 20.8466 17.2429C20.8466 17.6189 20.5421 17.9234 20.1661 17.9234ZM24.4328 22.9742H22.8881C22.5121 22.9742 22.2076 22.6697 22.2076 22.2937C22.2076 21.9178 22.5121 21.6132 22.8881 21.6132H24.4328C24.8088 21.6132 25.1133 21.9178 25.1133 22.2937C25.1133 22.6689 24.8088 22.9742 24.4328 22.9742ZM24.4328 20.4487H22.8881C22.5121 20.4487 22.2076 20.1442 22.2076 19.7682C22.2076 19.3922 22.5121 19.0877 22.8881 19.0877H24.4328C24.8088 19.0877 25.1133 19.3922 25.1133 19.7682C25.1133 20.1442 24.8088 20.4487 24.4328 20.4487ZM24.4328 17.9232H22.8881C22.5121 17.9232 22.2076 17.6187 22.2076 17.2427C22.2076 16.8667 22.5121 16.5622 22.8881 16.5622H24.4328C24.8088 16.5622 25.1133 16.8667 25.1133 17.2427C25.1133 17.6187 24.8088 17.9232 24.4328 17.9232Z"
        fill="white"
      />
    </svg>
  );
}

function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const toggleLanguage = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
    document.documentElement.lang = newLang;
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
  };
  return (
    <button
      onClick={toggleLanguage}
      className="flex cursor-pointer items-center gap-1 rounded-md border px-2 py-1 text-sm"
    >
      <Globe size={16} />
      {i18n.language === 'ar' ? 'EN' : 'عربي'}
    </button>
  );
}

function CompanyLogo({ className }: { className?: string }) {
  const user = useLoaderData() as Awaited<ReturnType<typeof layoutLoader>>;
  const classes = ['rounded-full object-cover', className]
    .filter(Boolean)
    .join(' ');
  if (user.companyDocuments?.companyLogo) {
    return (
      <img
        src={user.companyDocuments.companyLogo}
        alt="logo"
        className={classes}
        loading="lazy"
      />
    );
  }
  return <DefaultLogo className={className} />;
}

function NotificationBell() {
  const { t } = useTranslation();
  const notificationsQuery = useData('GET /notifications', {
    read: 'false',
    page: 1,
    pageSize: 5,
  });

  const markAsReadAction = useAction('PUT /notifications/{id}/read', {
    invalidate: ['GET /notifications'],
  });

  const unreadCount = notificationsQuery.data?.totalCount ?? 0;
  const notifications = notificationsQuery.data?.records ?? [];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="relative p-2"
        aria-label={t('header.notifications')}
      >
        <Bell size={20} className="text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -start-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80">
        <div className="border-b px-4 py-2 text-end font-bold">
          {t('header.notifications')}
        </div>
        {notifications.length === 0 ? (
          <div className="px-4 py-6 text-center text-gray-500">
            {t('header.noNotifications')}
          </div>
        ) : (
          <>
            {notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                asChild
                className="cursor-pointer"
                onClick={() => markAsReadAction.mutate({ id: notification.id })}
              >
                <Link
                  to={notification.actionUrl || '/'}
                  className="flex flex-col items-end gap-1 px-4 py-3 text-end hover:bg-gray-50"
                >
                  <p className="text-sm font-semibold">{notification.title}</p>
                  <p
                    className="line-clamp-2 text-end text-xs text-gray-600"
                    dir="auto"
                  >
                    {notification.message}
                  </p>
                </Link>
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Header() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useLoaderData() as Awaited<ReturnType<typeof layoutLoader>>;
  return (
    <div className="page-shadow flex items-center justify-between rounded-lg bg-white px-12 py-6">
      <p>
        <span>{t('header.welcome')}</span>
        <span> </span>
        <span className="font-bold">{user.name}</span>
      </p>
      <div className="flex items-center gap-4">
        <LanguageSwitcher />
        <NotificationBell />
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-2">
            <CompanyLogo className="size-8" />
            <p>{user.name}</p>
            <ChevronDown className="mt-1 size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem asChild className="justify-end py-2">
              <div className="justify-end py-2">
                <Link to="/profile">{t('header.profileData')}</Link>
                <UserRound className="text-black" />
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="justify-end py-2"
              onClick={() =>
                logout(auth).then(() => navigate('/signin', { replace: true }))
              }
            >
              {t('header.logout')}
              <LogOut className="text-black" />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

function TermsGate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userQuery = useUser();
  const acceptTermsAction = useAction('POST /customer/me/accept-terms', {
    invalidate: ['GET /customer/me'],
    meta: { toast: true },
  });

  return (
    <AcceptTermsDialog
      open={!!userQuery.data && !userQuery.data.termsAcceptedAt}
      isPending={acceptTermsAction.isPending}
      onAccept={() => acceptTermsAction.mutate({})}
      onLogout={() =>
        logout(auth).then(() => navigate('/signin', { replace: true }))
      }
      documents={[
        {
          href: 'https://medhub.tgl.com.sa/customer-agreement',
          label: t('signup.customerAgreement'),
          kind: 'agreement',
        },
        {
          href: 'https://medhub.tgl.com.sa/terms-and-privacy',
          label: t('signup.privacyPolicy'),
          kind: 'privacy',
        },
      ]}
    />
  );
}

export default function Layout() {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
  }, [i18n.language]);

  return (
    <SharedLayout
      items={[
        {
          href: '/',
          icon: <LayoutDashboard strokeWidth={2} />,
          label: t('nav.dashboard'),
        },
        {
          href: '/requests',
          icon: <ClipboardList strokeWidth={2} />,
          label: t('nav.myRequests'),
        },
        {
          href: '/profile',
          icon: <UserRound strokeWidth={2} />,
          label: t('nav.profile'),
        },
      ]}
    >
      <Header />
      <TermsGate />
    </SharedLayout>
  );
}

export async function layoutLoader() {
  const identity = await requiresAuth();
  if (!identity.contactId?.trim() || !identity.companyDocumentsId?.trim()) {
    throw redirect('/signup');
  }
  return identity;
}
